import { useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  BooleanField,
  SearchInput,
  SelectInput,
  FilterButton,
  TopToolbar,
  ExportButton,
  CreateButton,
  useRecordContext,
  useNotify,
  FunctionField,
} from 'react-admin';
import { Button } from '@mui/material';
import { LockReset as ResetIcon } from '@mui/icons-material';
import { RoleChip } from '../../components/settings/RoleChip';
import { PasswordResetDialog } from '../../components/settings/PasswordResetDialog';

interface AdminUser {
  id: number;
  username: string;
  email: string;
  role_id: number;
  role_name?: string;
  is_active: boolean;
  created_at: string;
  last_login?: string;
}

const adminUserFilters = [
  <SearchInput
    key="search"
    source="search"
    alwaysOn
    placeholder="Search username or email"
  />,
  <SelectInput
    key="role"
    source="role_id"
    label="Role"
    choices={[
      { id: 1, name: 'Super Admin' },
      { id: 2, name: 'Admin' },
      { id: 3, name: 'Support' },
      { id: 4, name: 'Viewer' },
    ]}
  />,
  <SelectInput
    key="is_active"
    source="is_active"
    label="Status"
    choices={[
      { id: true, name: 'Active' },
      { id: false, name: 'Inactive' },
    ]}
  />,
];

const AdminUserListActions = () => (
  <TopToolbar>
    <FilterButton />
    <CreateButton />
    <ExportButton />
  </TopToolbar>
);

const ResetPasswordButton = () => {
  const record = useRecordContext<AdminUser>();
  const notify = useNotify();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tempPassword, setTempPassword] = useState('');

  if (!record) return null;

  const handleResetPassword = async () => {
    try {
      // TODO: Call API to reset password
      // const response = await dataProvider.invoke('admin-users', {
      //   id: record.id,
      //   method: 'reset-password',
      // });

      // Mock temporary password for now
      const mockPassword = `Temp${Math.random().toString(36).slice(-8)}!`;
      setTempPassword(mockPassword);
      setDialogOpen(true);

      notify('Password reset successfully', { type: 'success' });
    } catch (error: any) {
      notify(`Error resetting password: ${error.message}`, { type: 'error' });
    }
  };

  return (
    <>
      <Button
        size="small"
        startIcon={<ResetIcon />}
        onClick={handleResetPassword}
        sx={{ textTransform: 'none' }}
      >
        Reset
      </Button>
      <PasswordResetDialog
        open={dialogOpen}
        username={record.username}
        tempPassword={tempPassword}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
};

export const AdminUserList = () => (
  <List
    filters={adminUserFilters}
    actions={<AdminUserListActions />}
    perPage={50}
    sort={{ field: 'created_at', order: 'DESC' }}
  >
    <Datagrid rowClick="show" bulkActionButtons={false}>
      <TextField source="id" label="ID" />
      <TextField source="username" />
      <TextField source="email" />
      <FunctionField
        source="role_name"
        label="Role"
        render={(record: AdminUser) => (
          <RoleChip roleName={record.role_name || 'Unknown'} />
        )}
      />
      <BooleanField source="is_active" label="Active" />
      <DateField source="last_login" showTime label="Last Login" />
      <DateField source="created_at" showTime label="Created" />
      <FunctionField
        label="Actions"
        render={(record: AdminUser) => <ResetPasswordButton />}
      />
    </Datagrid>
  </List>
);
